import Immutable from 'immutable';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { ConnectDragSource } from 'react-dnd';
import { useSelector, useDispatch } from 'react-redux';
import SpacerActions from 'components/SpacerActions';
import { Layer } from 'const/Layer';
import { setMoveableLayoutId } from 'containers/App/actions';
import { deleteLayoutRequest } from 'containers/Layouts/actions';
import { updateLayeredRelations } from 'containers/Relations/actions';
import * as Models from 'models';
import { LayoutMap } from 'models';
import { toPx } from 'utils/toPx';
import { spacerSelector } from './selectors';
import styles from './styles.module.scss';

type Props = {
  connectDragSource: ConnectDragSource;
  layout: LayoutMap;
  notEditable: boolean;
  dataTestId?: string;
};

const Spacer: React.FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const { connectDragSource, layout, notEditable, dataTestId = 'spacer' } = props;
  const { areActionsDisabled, relations, scale } = useSelector(spacerSelector);
  const layoutId = layout.get('id');
  const layoutRelationId = layout.get('relationId');
  const layoutRelation = relations.get(layoutRelationId) as Models.ColumnRelationMap;
  const rowRelationId = layoutRelation.get('relationIds').first();
  const rowRelation = relations.get(rowRelationId) as Models.RowRelationMap;
  const relationId = rowRelation.get('relationIds').first();
  const relation = relations.get(relationId) as Models.LayeredRegularRelationMap<Models.SpacerRelationStyles>;
  const container = React.createRef<HTMLDivElement>();
  // Spacer element stores styles on original layer only (styles are shared between layers)
  const layer = Layer.ORIGINAL;

  const deleteSpacer = useCallback(
    () => {
      dispatch(deleteLayoutRequest(layoutId));
    },
    [layoutId, dispatch],
  );

  const setMoveableAsset = useCallback(
    (isDraggable: boolean) => {
      dispatch(setMoveableLayoutId(isDraggable ? layoutId : ''));
    },
    [layoutId, dispatch],
  );

  const [height, setHeight] = useState(() => relation.getIn(['styles', layer, 'height']));

  const resize = useCallback(
    ({ clientY }: DragEvent): void => {
      if (!container.current) {
        return;
      }
      const { bottom } = container.current.getBoundingClientRect();
      const change = bottom - clientY;
      const newHeight = height - change / scale;

      if (newHeight > 0) {
        setHeight(newHeight);
      }
    },
    [container, height, scale]);

  const finishResize = useCallback((): void => {
    const updatedRelation = relation.setIn(['styles', layer, 'height'], height);
    dispatch(updateLayeredRelations(Immutable.Map({ [updatedRelation.get('id')]: updatedRelation }) as Models.LayeredRegularRelationsMap));
  }, [relation, layer, height, dispatch]);

  return (
    <>
      <div
        className={styles.Spacer}
        style={{ height: toPx(height) }}
        ref={container}
        data-testid={dataTestId}
      />
      {
        !notEditable &&
        <SpacerActions
          connectDragSource={connectDragSource}
          deleteLayout={deleteSpacer}
          finishResize={finishResize}
          areActionsDisabled={areActionsDisabled}
          resize={resize}
          setMoveableAsset={setMoveableAsset}
          spacerHeight={_.round(height)}
          dataTestId={`${dataTestId}-actions`}
        />
      }
    </>
  );
};

export default Spacer;
